import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Search as SearchIcon, Loader2, TrendingUp, Film, Tv, Calendar, Star, BookOpen, Gamepad2, Plus } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "../i18n";
import { CreateItemDialog } from "../components/CreateItemDialog";

const API = '/api';

function useMediaTypeConfig() {
  const { t } = useTranslation();
  return {
    movie: { icon: Film, label: t.mediaTypes.movie, color: "bg-blue-500/10 text-blue-500" },
    tv_series: { icon: Tv, label: t.mediaTypes.tv_series, color: "bg-purple-500/10 text-purple-500" },
    book: { icon: BookOpen, label: t.mediaTypes.book, color: "bg-green-500/10 text-green-500" },
    video_game: { icon: Gamepad2, label: t.mediaTypes.video_game, color: "bg-orange-500/10 text-orange-500" },
  };
}

function SearchResultCard({ item, index }) {
  const mediaTypeConfig = useMediaTypeConfig();
  const config = mediaTypeConfig[item.media_type] || mediaTypeConfig.movie;
  const TypeIcon = config.icon;
  
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}>
      <Link to={`/media/${item.item_id}`} data-testid={`search-result-${item.item_id}`}>
        <Card className="overflow-hidden card-hover h-full active:scale-[0.98] transition-transform">
          <div className="flex h-full">
            <div className="w-20 md:w-24 flex-shrink-0 relative">
              {item.poster_url || item.cover_image ? (
                <img src={item.poster_url || item.cover_image} alt={item.title} className="w-full h-full object-cover min-h-[120px] md:min-h-[140px]" loading="lazy" />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center min-h-[120px] md:min-h-[140px]">
                  <TypeIcon className="w-6 h-6 md:w-8 md:h-8 text-muted-foreground/50" />
                </div>
              )}
              {item.vote_average > 0 && (
                <div className="absolute top-1.5 left-1.5 md:top-2 md:left-2 bg-black/70 rounded px-1 py-0.5 flex items-center gap-0.5">
                  <Star className="w-2.5 h-2.5 md:w-3 md:h-3 fill-yellow-400 text-yellow-400" />
                  <span className="text-[10px] md:text-xs text-white font-medium">{item.vote_average.toFixed(1)}</span>
                </div>
              )}
            </div>
            <CardContent className="flex-1 p-2.5 md:p-3 flex flex-col min-w-0">
              <h3 className="font-semibold text-xs md:text-sm line-clamp-2 leading-tight mb-1">{item.title}</h3>
              <div className="flex flex-wrap items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                <Badge variant="secondary" className={`text-[10px] md:text-xs px-1.5 py-0 ${config.color}`}>
                  <TypeIcon className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5" />
                  {config.label}
                </Badge>
                {item.release_year && (
                  <span className="text-[10px] md:text-xs text-muted-foreground">{item.release_year}</span>
                )}
              </div>
              {(item.overview || item.description) && (
                <p className="text-[10px] md:text-xs text-muted-foreground line-clamp-2 flex-1 hidden sm:block">
                  {item.overview || item.description}
                </p>
              )}
            </CardContent>
          </div>
        </Card>
      </Link>
    </motion.div>
  );
}

function TrendingCard({ item, index }) {
  const mediaTypeConfig = useMediaTypeConfig();
  const config = mediaTypeConfig[item.media_type] || mediaTypeConfig.movie;
  const TypeIcon = config.icon;
  
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}>
      <Link to={`/media/${item.item_id}`} className="group block" data-testid={`trending-${item.item_id}`}>
        <div className="relative aspect-[2/3] overflow-hidden rounded-sm border border-border card-hover bg-muted">
          {item.poster_url || item.cover_image ? (
            <img src={item.poster_url || item.cover_image} alt={item.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><TypeIcon className="w-12 h-12 text-muted-foreground/30" /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <p className="text-white text-sm font-medium truncate">{item.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className="text-xs bg-white/20 text-white border-0">{config.label}</Badge>
                {item.vote_average > 0 && <span className="text-xs text-white/80 flex items-center gap-1"><Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />{item.vote_average.toFixed(1)}</span>}
              </div>
            </div>
          </div>
          {item.vote_average > 0 && (
            <div className="absolute top-2 right-2 bg-black/60 rounded px-1.5 py-0.5 flex items-center gap-1 group-hover:opacity-0 transition-opacity">
              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" /><span className="text-xs text-white font-medium">{item.vote_average.toFixed(1)}</span>
            </div>
          )}
        </div>
        <div className="mt-2 space-y-1">
          <h3 className="text-sm font-medium truncate group-hover:text-primary transition-colors">{item.title}</h3>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="font-mono uppercase tracking-wider">{config.label}</span>
            {item.release_year && <><span>•</span><span className="font-mono">{item.release_year}</span></>}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function Search({ user }) {
  const { t, lang } = useTranslation();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const mediaTypes = [
    { value: "movie", label: t.mediaTypesPlural.movie },
    { value: "tv_series", label: t.mediaTypesPlural.tv_series },
    { value: "book", label: t.mediaTypesPlural.book },
    { value: "video_game", label: t.mediaTypesPlural.video_game },
  ];
  // Restore search state from sessionStorage
  const savedSearch = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("search_state") || "null"); } catch { return null; }
  }, []);

  const [query, setQuery] = useState(savedSearch?.query || "");
  const [mediaType, setMediaType] = useState(savedSearch?.mediaType || "movie");
  const [results, setResults] = useState(savedSearch?.results || []);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(savedSearch?.hasSearched || false);
  const [trending, setTrending] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(false);

  // Save search state
  useEffect(() => {
    sessionStorage.setItem("search_state", JSON.stringify({ query, mediaType, results, hasSearched }));
  }, [query, mediaType, results, hasSearched]);
  
  useEffect(() => { fetchTrending(); }, []);
  
  const fetchTrending = async () => {
    setTrendingLoading(true);
    try {
      const response = await axios.get(`${API}/trending?media_type=all&lang=${lang}`);
      setTrending(response.data.results || []);
    } catch (error) { console.error("Error fetching trending:", error); }
    finally { setTrendingLoading(false); }
  };
  
  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.get(`${API}/search`, { params: { query: query.trim(), media_type: mediaType, lang } });
      setResults(response.data.results || []);
    } catch (error) { console.error("Error searching:", error); toast.error(t.search.searchFailed); }
    finally { setLoading(false); }
  }, [query, mediaType]);
  
  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="search-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{t.search.title}</h1>
        <p className="text-sm text-muted-foreground mt-0.5">{t.search.subtitle}</p>
      </div>
      
      <form onSubmit={handleSearch} className="space-y-3" data-testid="search-form">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
          <Input 
            type="text" 
            placeholder={t.search.placeholder} 
            value={query} 
            onChange={(e) => setQuery(e.target.value)} 
            className="pl-10 h-12 text-base min-h-[48px]" 
            data-testid="search-input" 
          />
        </div>
        <div className="flex gap-2">
          <Select value={mediaType} onValueChange={setMediaType}>
            <SelectTrigger className="flex-1 h-11 min-h-[44px]" data-testid="media-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {mediaTypes.map((type) => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button 
            type="submit" 
            disabled={loading || !query.trim()} 
            className="min-w-[80px] min-h-[44px]"
            data-testid="search-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t.search.searchButton}
          </Button>
        </div>
      </form>
      
      {loading ? (
        <div className="flex items-center justify-center min-h-[30vh]">
          <div className="animate-pulse text-muted-foreground">{t.search.searching}</div>
        </div>
      ) : hasSearched && results.length > 0 ? (
        <div className="space-y-3 md:space-y-4" data-testid="search-results">
          <p className="text-xs md:text-sm text-muted-foreground">{t.search.resultsFor(results.length, query)}</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-3">
            {results.map((item, index) => (<SearchResultCard key={item.item_id} item={item} index={index} />))}
          </div>
        </div>
      ) : hasSearched ? (
        <div className="text-center py-10 md:py-12" data-testid="no-results">
          <SearchIcon className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-base md:text-lg font-semibold mb-2">{t.search.noResults}</h3>
          <p className="text-sm text-muted-foreground mb-4">{t.search.noResultsFor(query)}</p>
          <Button variant="outline" onClick={() => setCreateDialogOpen(true)} data-testid="create-new-title-btn">
            <Plus className="w-4 h-4 mr-2" />{t.search.createNewTitle}
          </Button>
        </div>
      ) : (
        <div className="space-y-3 md:space-y-4" data-testid="trending-section">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-primary" />
            <h2 className="text-base md:text-xl font-semibold">{t.search.trendingThisWeek}</h2>
          </div>
          {trendingLoading ? (
            <div className="flex items-center justify-center min-h-[20vh]">
              <div className="animate-pulse text-muted-foreground">{t.search.loadingTrending}</div>
            </div>
          ) : trending.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {trending.slice(0, 6).map((item, index) => (<TrendingCard key={item.item_id} item={item} index={index} />))}
              <div className="hidden md:contents">
                {trending.slice(6).map((item, index) => (<TrendingCard key={item.item_id} item={item} index={index + 6} />))}
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8 text-sm">{t.search.noTrending}</p>
          )}
        </div>
      )}

      <CreateItemDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} onSuccess={(item) => { setCreateDialogOpen(false); window.location.href = `/media/${item.item_id}`; }} />
    </div>
  );
}

export default Search;
